import React = require('react');
import { getSessionManagementServiceInstance } from '../../sessionManagement/SessionManagementService';
import { ServerSettingsUnauthenticated } from '../../settings/services/ServerSettingsUnauthenticated';
import { IServerUserInfo } from '../../userManagement/users/models/IServerUserInfo';
import { ISiteUserInfo } from '../../userManagement/users/models/ISiteUserInfo';
import { IUserSettings } from '../models/IUserSettings';
import { UserSettingsStyle } from '../styles/UserSettingsStyle';
import { GeneralAuthSettings, GeneralAuthSettingsProps } from './GeneralAuthSettings';
import { LanguageAndRegionSettings,LanguageAndRegionSettingsProps } from './LanguageAndRegionSettings';
import { NotificationsSettings, NotificationsSettingsProps } from './NotificationsSettings';
import { PreferenceSettingProps, PreferenceSettings } from './PreferenceSettings';
import { SecuritySettings,SecuritySettingsProps } from './SecuritySettings';

export interface UserSettingsSubspaceProps {
  userSettings: IUserSettings;
  userRefreshTokenCount: number;
  isSessionUserSelf: boolean;
  serverUser: IServerUserInfo;
  siteUser?: ISiteUserInfo;
  isServerUserSubplace?: boolean;
}

export function UserSettingsSubspace(props: UserSettingsSubspaceProps): JSX.Element {
  const sessionManagementService = getSessionManagementServiceInstance();
  const isDomainFamilyAD = ServerSettingsUnauthenticated.isServerDomainFamilyActiveDirectory();
  const isDomainFamilyExternal = ServerSettingsUnauthenticated.isServerDomainFamilyExternal();
  const isDomainFamilyLocal = ServerSettingsUnauthenticated.isServerDomainFamilyLocal();
  const updateDisplayNameAllowed = sessionManagementService.updateDisplayNameAllowed();
  const changePasswordUrl = sessionManagementService.changePasswordUrl();
  const manageMfaUrl = sessionManagementService.manageMfaUrl();
  const isUserSelf = sessionManagementService.isSessionUserSelf(props.serverUser.username, props.serverUser.domainName);
  const isViewerServerAdmin = sessionManagementService.isSessionServerAdmin();
  const isViewerAdmin = sessionManagementService.isSessionSiteOrServerAdmin();
  const sessionSiteUrlName = sessionManagementService.sessionSiteUrlName();
  const welcomeChannelEnabled = sessionManagementService.isWelcomeChannelEnabledForUser();
  const userNotificationsPlatformEnabled = sessionManagementService.isUserNotificationsPlatformEnabled();

  const generalAuthSettingsProps: GeneralAuthSettingsProps = {
    isDomainFamilyLocal: isDomainFamilyLocal,
    isDomainFamilyExternal: isDomainFamilyExternal,
    isDomainFamilyAD: isDomainFamilyAD,
    emailFormInitialValues: {
      email: props.userSettings.email,
      displayName: props.serverUser.displayName
    },
    updateDisplayNameAllowed: updateDisplayNameAllowed,
    siteUser: props.siteUser,
    serverUser: props.serverUser,
    isViewerServerAdmin: isViewerServerAdmin,
    isViewerSelf: props.isSessionUserSelf,
    changePasswordUrl: changePasswordUrl,
    manageMfaUrl: manageMfaUrl,
    isUserSelf: isUserSelf,
    isViewerAdmin: isViewerAdmin
  };

  const securitySettingsProps: SecuritySettingsProps = {
    serverUser: props.serverUser,
    isViewerSelf: props.isSessionUserSelf,
    isServerUserSubplace: props.isServerUserSubplace
  };

  const preferenceSettingsProps: PreferenceSettingProps = {
    isViewerSelf: props.isSessionUserSelf,
    userHasNoSiteId: (!props.siteUser || !props.siteUser.id),
    siteUser: props.siteUser,
    welcomeChannelEnabled: welcomeChannelEnabled,
    startPage: props.userSettings.startPage,
    isViewerAdmin: isViewerAdmin,
    serverUser: props.serverUser,
    isServerUserSubplace: props.isServerUserSubplace,
    sessionSiteUrlName: sessionSiteUrlName
  };

  const languageAndRegionSettingsProps: LanguageAndRegionSettingsProps = {
    languageInitialValues: {language: props.userSettings.language},
    localeInitialValues: {locale: props.userSettings.locale},
    timeZoneInitialValues: {timeZoneId: props.userSettings.timeZoneId},
    serverUser: props.serverUser,
    isServerUserSubplace: props.isServerUserSubplace
  };

  const notificationsSettingsProps: NotificationsSettingsProps = {
    siteUser: props.siteUser,
    serverUser: props.serverUser,
    isViewerAdmin: isViewerAdmin,
    userHasNoSiteId: (!props.siteUser || !props.siteUser.id),
    isUserNotificationsPlatformEnabled: userNotificationsPlatformEnabled,
  };

  return (
    <div className={UserSettingsStyle.userSettingsRoot}>
      <table className="tb-root-table">
        {getSectionRow(<GeneralAuthSettings {...generalAuthSettingsProps}/>)}
        {getSectionRow(<SecuritySettings {...securitySettingsProps}/>)}
        {getSectionRow(<NotificationsSettings {...notificationsSettingsProps}/>)}
        {getSectionRow(<PreferenceSettings {...preferenceSettingsProps}/>)}
        {getSectionRow(<LanguageAndRegionSettings {...languageAndRegionSettingsProps}/>)}
      </table>
    </div>
  );

}

function getSectionRow(section: JSX.Element): JSX.Element {
  return(
  <tbody>
    <tr>
      <td className="tb-nested-table" colSpan={3}>
        {section}
      </td>
    </tr>
  </tbody>);
}